import IconTrashBin from "assets/img/icon-trash-bin.svg?react"
import IconCopy from "assets/img/icon-copy.svg?react"
import IconPencil from "assets/img/icon-pencil.svg?react"
import IconClose from "assets/img/icon-close.svg?react"
import IconReopen from "assets/img/icon-reset.svg?react"
import IconEarlyReturn from "assets/img/icon-early-return.svg?react"

import { useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"

import Button from "components/Button/Button"
import Routes from "@/routes"
import ReservationCopyModal from "components/ReservationCopyModal/ReservationCopyModal"
import { ReservationCartCopyFlag } from "models/ReservationCart"
import { InfoDropdown, InfoDropdownList, InfoDropdownListItemButton } from "components/InfoDropdown"
import { Flex, FlexItem } from "components/Flex"
import OfferCreateModal from "components/OfferCreateModal/OfferCreateModal"
import EarlyReturnModal from "pages/RequestsPage/EarlyReturnModal"

interface IReservationSidebarActionButtonsProps {
    reservationId: string
    reservationNumber: string
    canEarlyReturn?: boolean
    canCopyAsReservation?: boolean
    canCopyAsOffer?: boolean
    canEdit?: boolean
    onDeleteClick?(): void
    onReopenClick?(): void
    onCloseClick(): void
}

const ReservationSidebarActionButtons = ({
    reservationId,
    reservationNumber,
    canEarlyReturn,
    canCopyAsReservation,
    canCopyAsOffer,
    canEdit,
    onDeleteClick,
    onReopenClick,
    onCloseClick,
}: IReservationSidebarActionButtonsProps) => {
    const { t } = useTranslation()
    const location = useLocation()
    const navigate = useNavigate()
    const [showEarlyReturnModal, setShowEarlyReturnModal] = useState(false)
    const [showCopyModal, setShowCopyModal] = useState(false)
    const [showCreateOfferModal, setShowCreateOfferModal] = useState(false)

    function handleCopy(copyFlag?: ReservationCartCopyFlag) {
        setShowCopyModal(false)
        navigate(
            Routes.reservationCopy.getPath({
                reservationId
            }, {
                copyFlag,
                backUrl: location.pathname + location.search
            })
        )
    }

    if (!canEarlyReturn && !canCopyAsReservation && !canCopyAsOffer && !canEdit && !onReopenClick && !onDeleteClick) {
        return (
            <Button
                onClick={() => onCloseClick()}
                icon={<IconClose />}
                theme="transparent"
                round
                aria-label="Close"
                data-testid="modal-close"
            />
        )
    }

    return (
        <>
            <Flex gap="1.6rem">
                {typeof onDeleteClick === "function" &&
                    <Button
                        ariaLabel={t("common.delete")}
                        round
                        theme="grey"
                        icon={<IconTrashBin />}
                        onClick={onDeleteClick}
                    />
                }
                {canEarlyReturn &&
                    <Button
                        ariaLabel={t("earlyReturn.earlyReturn")}
                        round
                        theme="grey"
                        icon={<IconEarlyReturn />}
                        onClick={() => setShowEarlyReturnModal(true)}
                    />
                }
                {(canCopyAsReservation || canCopyAsOffer) &&
                    <InfoDropdown
                        label={() => (
                            <Button
                                ariaLabel={t("common.copy")}
                                round
                                theme="grey"
                                icon={<IconCopy />}
                            />
                        )}
                        placement="bottom"
                        content={closeInfoDropdown => (
                            <InfoDropdownList>
                                {canCopyAsReservation &&
                                    <InfoDropdownListItemButton
                                        onClick={() => { closeInfoDropdown(); setShowCopyModal(true) }}>
                                        <Flex gap="0.8rem" alignItems="center">
                                            <FlexItem shrink={0}>
                                                <IconCopy className="d-block" />
                                            </FlexItem>
                                            {t("reservation.copyAsNewRequest")}
                                        </Flex>
                                    </InfoDropdownListItemButton>
                                }
                                {canCopyAsOffer &&
                                    <InfoDropdownListItemButton
                                        onClick={() => { closeInfoDropdown(); setShowCreateOfferModal(true) }}>
                                        <Flex gap="0.8rem" alignItems="center">
                                            <FlexItem shrink={0}>
                                                <IconCopy className="d-block" />
                                            </FlexItem>
                                            {t("reservation.copyAsNewOffer")}
                                        </Flex>
                                    </InfoDropdownListItemButton>
                                }
                            </InfoDropdownList>
                        )}
                    />
                }
                {typeof onReopenClick === "function" &&
                    <Button
                        ariaLabel={t("reservation.reopenRequest")}
                        round
                        theme="grey"
                        icon={<IconReopen />}
                        onClick={onReopenClick}
                    />
                }
                {!!canEdit &&
                    <Button
                        type="link"
                        url={Routes.reservationEdit.getPath({ reservationId }, { backUrl: location.pathname + location.search })}
                        ariaLabel={t("common.edit")}
                        round
                        theme="grey"
                        icon={<IconPencil />}
                    />
                }
                {showCopyModal &&
                    <ReservationCopyModal
                        reservationNumber={reservationNumber}
                        onCopy={handleCopy}
                        onClose={() => setShowCopyModal(false)}
                    />
                }
            </Flex>
            {showCreateOfferModal &&
                <OfferCreateModal
                    onClose={() => setShowCreateOfferModal(false)}
                    copyReservationId={reservationId}
                />
            }
            {showEarlyReturnModal &&
                <EarlyReturnModal
                    reservationId={reservationId}
                    onClose={() => setShowEarlyReturnModal(false)}
                />
            }
        </>
    )
}

export default ReservationSidebarActionButtons
